import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Classification, classifications} from "../classifications";
import {isStaticSite, storage} from "../electron-modules";
import {cloneDeep} from 'lodash';
import { isMobile } from '../viewer/js/utilities';

export interface Clamp {
  min: number;
  max: number;
}

export type ColorMapKey = 'heat-map' | 'awesome-green' | 'pastel-shades' | 'blue-orange' |
  'black-orange' | 'blue-hue' | 'blue-red' | 'jet' | 'grayscale';

export enum ColorType {
  Intensity,
  RGB,
  Height,
  Classification
}

export enum MarkerNavType {
  Arrows,
  Markers,
  None
}

export interface ClassificationSetting extends Classification {
  visible: boolean;
  available: boolean;
}

export interface SettingsState {
  colorMap: ColorMapKey;
  colorType: ColorType;
  dynamicSize: boolean;
  circularPoints: boolean;
  arrowMarkers: MarkerNavType;
  drawerOpen: boolean;
  activeTab: number;
  opacity: number;
  heightClip: Clamp;
  intensityClip: Clamp;
  cloudScale: number;
  cloudMinimum: number;
  cloudMaxPoints: number;
  cloudDistance: number;
  tagDistance: number;
  classifications: ClassificationSetting[];
  switched: boolean;
}

const defaultIntensityClip = {
  min: 0,
  max: 1
};

const defaultHeightClip = {
  min: 0,
  max: 1
};

const defaultClassifications = classifications.map(classification => ({
  visible: true,
  available: false,
  ...classification
}));

export const initialSettingsState = {
  colorMap: 'jet',
  colorType: ColorType.Height,
  dynamicSize: true,
  circularPoints: true,
  arrowMarkers: MarkerNavType.Arrows,
  activeTab: 0,
  opacity: 0.5,
  cloudMaxPoints: 5,
  cloudDistance: 1000,
  cloudScale: 1.0,
  cloudMinimum: 2.0,
  tagDistance: 1000,
  switched: false,
  drawerOpen: !isMobile,
  heightClip: defaultHeightClip,
  intensityClip: defaultIntensityClip,
  classifications: defaultClassifications,
} as SettingsState;

const loadSavedDefaults = () => {
  if (isStaticSite) return {};

  return storage.getSync("default-settings");
};

const saveSettingsDefaults = (state) => {
  if (isStaticSite) return;

  const copy = cloneDeep(state);

  // Remove anything that doesn't need defaults
  delete copy.classifications;
  delete copy.activeTab;

  storage.setSync("default-settings", copy);
};

const applySavedSettings = (state, settings) => {
  const keys = Object.keys(settings);
  keys.forEach(key => {
    if (state.hasOwnProperty(key)) {
      state[key] = settings[key];
    }
  });
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState: initialSettingsState,
  reducers: {
    updateSettingsState: (state, action) => {
      applySavedSettings(state, action.payload);
    },
    applySavedDefaults: (state, action) => {
      // Apply any forced defaults
      state.activeTab = 0;

      // Apply saved defaults
      const defaults = loadSavedDefaults();
      applySavedSettings(state, defaults);
    },
    changeDefaultSettings: (state, action) => {
      saveSettingsDefaults(state);
    },
    changeOpacity: (state, action: PayloadAction<number>) => {
      state.opacity = action.payload;
      saveSettingsDefaults(state);
    },
    changeTagDistance: (state, action: PayloadAction<number>) => {
      state.tagDistance = action.payload;
      saveSettingsDefaults(state);
    },
    changeCloudDistance: (state, action: PayloadAction<number>) => {
      state.cloudDistance = action.payload;
      saveSettingsDefaults(state);
    },
    changeCloudMaxPoints: (state, action: PayloadAction<number>) => {
      state.cloudMaxPoints = action.payload;
      saveSettingsDefaults(state);
    },
    changeCloudScale: (state, action: PayloadAction<number>) => {
      state.cloudScale = action.payload;
      saveSettingsDefaults(state);
    },
    changeCloudMinimum: (state, action: PayloadAction<number>) => {
      state.cloudMinimum = action.payload;
      saveSettingsDefaults(state);
    },
    changeHeightClip: (state, action: PayloadAction<Clamp>) => {
      state.heightClip = action.payload;
      saveSettingsDefaults(state);
    },
    changeIntensityClip: (state, action: PayloadAction<Clamp>) => {
      state.intensityClip = action.payload;
      saveSettingsDefaults(state);
    },
    changeColorMap: (state, action: PayloadAction<ColorMapKey>) =>  {
      state.colorMap = action.payload;
      saveSettingsDefaults(state);
    },
    changeColorType: (state, action: PayloadAction<ColorType>) => {
      state.colorType = action.payload;
      saveSettingsDefaults(state);
    },
    changeDynamicSize: (state, action: PayloadAction<boolean>) => {
      state.dynamicSize = action.payload;
      saveSettingsDefaults(state);
    },
    changeCircularPoints: (state, action: PayloadAction<boolean>) => {
      state.circularPoints = action.payload;
      saveSettingsDefaults(state);
    },
    changeAvailableClassifications: (state, action: PayloadAction<number[]>) => {
      const availableClassifications = new Set(action.payload);
      state.classifications = state.classifications
        .map(classification => ({...classification, available: availableClassifications.has(classification.id)}));
    },
    changeClassificationVisibility: (state, action) => {
      const classification = state.classifications.find(classification => classification.id === action.payload);

      if (classification) {
        classification.visible = !classification.visible;
      }
    },
    changeAllClassificationVisibility: (state, action) => {
      state.classifications = state.classifications.map(classification => ({...classification, visible: action.payload}));
    },
    changeSwitched: (state, action: PayloadAction<boolean>) => {
      state.switched = action.payload;
      saveSettingsDefaults(state);
    },
    changeSelectedTab: (state, action: PayloadAction<number>) => {
      state.activeTab = action.payload;
      saveSettingsDefaults(state);
    },
    changeDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.drawerOpen = action.payload;
      saveSettingsDefaults(state);
    },
    changeArrowMarkers: (state, action: PayloadAction<MarkerNavType>) => {
      state.arrowMarkers = action.payload;
      saveSettingsDefaults(state);
    }
  }
});

export const {
  changeOpacity,
  changeTagDistance,
  changeCloudDistance,
  changeCloudMaxPoints,
  changeCloudScale,
  changeCloudMinimum,
  changeHeightClip,
  changeIntensityClip,
  changeColorMap,
  changeColorType,
  changeDynamicSize,
  changeCircularPoints,
  changeAvailableClassifications,
  changeClassificationVisibility,
  changeAllClassificationVisibility,
  changeSwitched,
  changeSelectedTab,
  changeDrawerOpen,
  changeArrowMarkers,
  updateSettingsState
} = settingsSlice.actions;

export default settingsSlice.reducer;

export const selectOpacity = state => state.settings.opacity;
export const selectTagDistance = state => state.settings.tagDistance;
export const selectCloudDistance = state => state.settings.cloudDistance;
export const selectCloudMaxPoints = state => state.settings.cloudMaxPoints;
export const selectCloudScale = state => state.settings.cloudScale;
export const selectCloudMinimum = state => state.settings.cloudMinimum;
export const selectHeightClip = state => state.settings.heightClip;
export const selectIntensityClip = state => state.settings.intensityClip;
export const selectColorMap = state => state.settings.colorMap;
export const selectColorType = state => state.settings.colorType;
export const selectDynamicSize = state => state.settings.dynamicSize;
export const selectCircularPoints = state => state.settings.circularPoints;
export const selectViewerSwitched = state => state.settings.switched;
export const selectSelectedTab = state => state.settings.activeTab;
export const selectDrawerOpen = state => state.settings.drawerOpen;
export const selectArrowMarkers = state => state.settings.arrowMarkers;

export const selectClassifications = state => state.settings.classifications;

export const getAvailableClassifications = classifications =>
  classifications.filter(classification => classification.available);

export const getClassificationVisibilities = classifications =>
  classifications.map(classification => ({
    visible: classification.visible,
    id: classification.id
  }));
